import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { baseUrl } from "@/api/config";
import NotificationContent from "@/components/notification/notificationContent";
import { MESSAGE } from "@/constants/message";
import {
  OperationStatus,
  OperationStatusType,
  OperationType
} from "@/types/dataset";

import useEventSource from "./useEventSource";

import useLoadingAndToast from "./ui/useLoadingAndToast";
import useToast, { EToastType } from "./ui/useToast";

export interface UseParsingStatusProps {
  onSuccess?: () => void;
}

interface UseParsingStatusReturn {
  isParsingActive: boolean;
  operation: OperationStatus | null;
  setIsParsingActive: React.Dispatch<React.SetStateAction<boolean>>;
  setOperationId: React.Dispatch<React.SetStateAction<string | null>>;
}

type UserDataOperationEvents = {
  dataset_operation_status: OperationStatus;
  dataset_detail: unknown;
  "dataset_data:meta": unknown;
  "dataset_data:chunk": unknown;
  "dataset_data:done": { dataset_id: string };
  "stream:reset": void;
  "stream:error": void;
  "stream:done": void;
};

export function useParsingStatus({
  onSuccess
}: UseParsingStatusProps): UseParsingStatusReturn {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showError } = useLoadingAndToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const lastEventID = searchParams.get("lastEventID");

  const [operationId, setOperationId] = useState<string | null>(
    searchParams.get("operationId")
  );
  const [isParsingActive, setIsParsingActive] = useState<boolean>(false);
  const [operation, setOperation] = useState<OperationStatus | null>(null);

  const getOperationStreamUrl = () => {
    const u = new URL(
      `${baseUrl}/v1/datasets/operations/${operationId}/stream`,
      window.location.origin
    );
    if (lastEventID) u.searchParams.set("lastEventID", lastEventID);
    return u.toString();
  };

  const handleFailedOperation = (failedOperation: OperationStatus) => {
    const { operation_type: operationType, error_message: errorMessage } =
      failedOperation;

    if (errorMessage?.includes("insufficient storage capacity")) {
      showError(
        <NotificationContent
          title="Upload Failed"
          description="File size exceeds your available quota. Please delete existing datasets or upgrade your plan"
        />
      );
    } else if (
      operationType === OperationType.IMPORT_ADD ||
      operationType === OperationType.IMPORT_REPLACE
    ) {
      showError(
        <NotificationContent
          title="Data Format Mismatch"
          description="Please ensure your file uses the same structure as the existing dataset"
        />
      );
    } else {
      showError(
        <NotificationContent
          title="Something Went Wrong"
          description="Sorry, we’re experiencing an issue while processing your request. Please try again."
        />
      );
    }

    setSearchParams(new URLSearchParams());
  };

  const resetFromServer = () => {
    setSearchParams(new URLSearchParams());
    setIsParsingActive(false);
  };

  useEventSource<UserDataOperationEvents>({
    eventKey: ["dataset_operation_status", { operationId }],
    url: getOperationStreamUrl(),
    enabled: !!operationId,
    parse: JSON.parse,
    closeOn: [
      "dataset_data:done",
      "stream:done",
      "stream:reset",
      "stream:error"
    ],
    onOpen: () => {
      setIsParsingActive(true);
    },

    eventHandlers: {
      // Progress/status updates during the operation
      dataset_operation_status: (data, event) => {
        setSearchParams(
          new URLSearchParams({
            lastEventID: event.lastEventId,
            operationId: operationId ?? ""
          })
        );

        setOperation(data);

        if (data.status === OperationStatusType.FAILED) {
          handleFailedOperation(data);
          setIsParsingActive(false);
        }
      },
      dataset_detail: (_, event) => {
        setSearchParams(
          new URLSearchParams({
            lastEventID: event.lastEventId,
            operationId: operationId ?? ""
          })
        );
      },
      "dataset_data:meta": (_, event) => {
        setSearchParams(
          new URLSearchParams({
            lastEventID: event.lastEventId,
            operationId: operationId ?? ""
          })
        );
      },
      "dataset_data:chunk": (_, event) => {
        setSearchParams(
          new URLSearchParams({
            lastEventID: event.lastEventId,
            operationId: operationId ?? ""
          })
        );
      },
      // Completion event
      "dataset_data:done": (data) => {
        onSuccess?.();
        navigate(`/dataset-management/${data.dataset_id}`);
        setIsParsingActive(false);
      },
      // Stream reset from server
      "stream:reset": resetFromServer,
      // Stream done from server
      "stream:done": resetFromServer,
      // Stream error event from server
      "stream:error": () => {
        setSearchParams(new URLSearchParams());

        showToast({
          type: EToastType.ERROR,
          message: (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold text-txtHeading">
                {MESSAGE.DATASET_UPLOAD_ERROR_TITLE}
              </p>
              <p className="text-sm text-txtHeading">
                {MESSAGE.DATASET_UPLOAD_ERROR_DESCRIPTION}
              </p>
            </div>
          ),
          autoClose: 2000
        });

        setIsParsingActive(false);
      }
    }
  });

  return {
    isParsingActive,
    operation,
    setIsParsingActive,
    setOperationId
  };
}
